import React from "react";
import { Button, ButtonProps } from "react-bootstrap";
import { Spinner } from "./Spinner";

// =============================================================================
// <Btn> — the canonical button for new code
// =============================================================================
//
// Use ONE of five semantic variants. Don't add a sixth — escalate the design
// instead. If you find yourself wanting a custom-styled button, the answer is
// almost always "use the right variant + better copy", not a new variant.
//
// VARIANT          MEANING                                  EXAMPLE
// -------------    ---------------------------------------  -----------------------
// primary          The single main CTA on a page/section.   "Save changes"
//                  Filled brand color. ONE per view.        "Convert documents"
//
// secondary        Supporting action, less emphasis than    "Add another field"
//                  primary. Outlined brand color.           "Configure columns"
//
// tertiary         Lower-emphasis action that still needs   "Cancel"
//                  to look like a button. Outlined gray.    "Reset filters"
//
// danger           Destructive or irreversible action.      "Delete job"
//                  Filled red. Always pair with a confirm.  "Cancel subscription"
//
// link             Inline text-style action. No button      "Forgot password?"
//                  shape. Use within paragraphs or          "Manage billing"
//                  alongside other text.
//
// PROPS WORTH KNOWING:
//   loading        Shows inline spinner, disables the button. Replaces the
//                  legacy LoadingButton/LoadingTrackedButton pattern in new code.
//   size           "sm" | "lg" — react-bootstrap sizes. Default is medium.
//   ...rest        Anything react-bootstrap Button accepts (className, onClick,
//                  type, disabled, etc.) is forwarded.
//
// ANALYTICS:
//   This component does NOT auto-fire GA events. If the action is
//   conversion-relevant (billing, export, signup, delete), wrap with
//   sendGAEvent({ action: "..." }) inside onClick — see the analytics audit
//   in commit 18213be for examples.
//
// MIGRATING FROM <Button>:
//   variant="primary"        → <Btn variant="primary">
//   variant="success"        → <Btn variant="primary">  (success was a synonym)
//   variant="secondary"      → <Btn variant="secondary"> or "tertiary"
//   variant="outline-*"      → <Btn variant="secondary"> or "tertiary"
//   variant="light"          → <Btn variant="tertiary">
//   variant="link"           → <Btn variant="link">
//   variant="danger"         → <Btn variant="danger">
// =============================================================================

export type BtnVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "danger"
  | "link";

// Map semantic variants to react-bootstrap's variant names. Centralised so the
// visual choice can change in one place (e.g. switch tertiary from
// outline-secondary to a pure ghost style).
const VARIANT_TO_BOOTSTRAP: Record<BtnVariant, NonNullable<ButtonProps["variant"]>> = {
  primary: "primary",
  secondary: "outline-primary",
  tertiary: "outline-secondary",
  danger: "danger",
  link: "link",
};

// Solid (filled) variants get a white spinner so it's visible on the colored
// fill. Outline / link variants get the default spinner color.
const SOLID_VARIANTS: ReadonlySet<BtnVariant> = new Set(["primary", "danger"]);

type Props = Omit<ButtonProps, "variant"> & {
  variant?: BtnVariant;
  loading?: boolean;
};

export const Btn: React.FC<Props> = ({
  variant = "primary",
  loading,
  disabled,
  children,
  ...rest
}) => {
  const bootstrapVariant = VARIANT_TO_BOOTSTRAP[variant];
  const spinnerColor = SOLID_VARIANTS.has(variant) ? "#fff" : undefined;
  return (
    <Button
      {...rest}
      variant={bootstrapVariant}
      disabled={disabled || loading}
    >
      {loading && (
        <span style={{ marginRight: "0.5rem", verticalAlign: "middle" }}>
          <Spinner size="sm" color={spinnerColor} />
        </span>
      )}
      {children}
    </Button>
  );
};

export default Btn;
